import { Search } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";

import tips from "../../../assets/copy/tips";
import ErrorToast from "../../../components/ErrorToast";
import EditorListItems, {
	type EditorListItem,
} from "../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import SectionActions from "../../../components/ResumeBuilder/SectionActions";
import SkipSectionDialog from "../../../components/ResumeBuilder/SkipSectionDialog";
import SuggestionsPopover from "../../../components/ResumeBuilder/SuggestionsPopover";
import Spinner from "../../../components/Spinner";
import TextEditor from "../../../components/TextEditor";
import Tips from "../../../components/Tips";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import ResumeContext from "../../../context/ResumeContext";
import { FormValidation } from "../../../handlers/formValidation";
import { NavigationHandler } from "../../../handlers/navigationHandler";
import { convertUlToArrayDefault } from "../../../handlers/utilityFunctions";
import { PreviousButton, resumeObjectToContextFormat } from "../../../handlers/valueTransformation";
import { StaticValue } from "../../../seed/constants";
import { usePatchResumeSection } from "../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../services/create-resume/resumeSections";
import type { ResumeContextValue } from "../../../types/resume";
import { allSummaries, summariesForJobFunction } from "./summaryMapping";

const ResumeBriefSummary = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();
	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const formValidation = new FormValidation();

	const patchSection = usePatchResumeSection();

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	let { journal } = resumeData;
	let resumeCurrentStatus = journal ? journal.currentStatus : false;
	let isComplete = resumeCurrentStatus == "COMPLETED";

	const [showSkipDialog, setShowSkipDialog] = useState(false);

	const previousTxt = PreviousButton(isComplete, "Extras", navigationHandler, 0);

	function previous() {
		navigationHandler.getPrevious(navigationHandler.template.navigationFlow, link, isComplete);
		navigate(-1);
	}

	const toEditorListItems = (titles: string[]): EditorListItem[] =>
		titles.map((title, index) => ({ id: index, title, added: false }));

	const allSummaryItems = useMemo<EditorListItem[]>(() => toEditorListItems(allSummaries()), []);

	// `summariesForJobFunction` resolves the dropdown's wording against the data
	// file's older keys. Matching them exactly left 14 of the 30 areas — Marketing
	// and Communications among them — staring at an empty suggestion panel.
	const functionSummaryItems = useMemo<EditorListItem[]>(
		() => toEditorListItems(summariesForJobFunction(resumeData.contactInfo.resumeFunction)),
		[resumeData.contactInfo.resumeFunction],
	);

	const listItems = useMemo(() => {
		if (resumeData.contactInfo.resumeFunction) {
			return functionSummaryItems;
		} else {
			return allSummaryItems;
		}
	}, [resumeData.contactInfo.resumeFunction]);

	const [summaryList, setSummaryList] = useState([...listItems]);

	useEffect(() => {
		setSummaryList([...functionSummaryItems]);
	}, [resumeData.contactInfo.resumeFunction]);

	const [searchValue, setSearchValue] = useState("");
	function filterSummaryList(input) {
		const filteredList = allSummaryItems.filter((item) => {
			return item.title.toLowerCase().includes(input.toLowerCase());
		});
		setSummaryList([...filteredList]);
	}

	useEffect(() => {
		if (searchValue.length > 0) {
			filterSummaryList(searchValue);
		} else {
			setSummaryList([...functionSummaryItems]);
		}
	}, [searchValue]);

	const [briefSummary, setBriefSummary] = useState(resumeData.summary);
	const [userInput, setUserInput] = useState(resumeData.contactInfo.resumeTitle);

	useEffect(() => {
		const addedItems = summaryList.filter((item) => {
			return item.added;
		});
		const notAddedItems = summaryList.filter((item) => {
			return !item.added;
		});
		setSummaryList([...addedItems, ...notAddedItems]);
	}, [briefSummary]);

	useEffect(() => {
		setBriefSummary(resumeData.summary);
	}, [resumeData]);

	const [isInputCompleted, setIsInputCompleted] = useState(false);

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();

	const [serverErrors, setServerErrors] = useState([]);

	let resumeId = cookies[staticValues.localStorageResumeId];

	// `progressStatus` is a server virtual, so the rail only learns a section is
	// finished from a ledger response. Dropping the one this save returns left
	// Professional Summary looking untouched until the next full load — the same
	// hydration the education and work-history saves already do.
	function onSuccess(ledger: unknown) {
		resumeObjectToContextFormat(ledger, setResumeData);
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate("/resume/extras");
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as { response: { data: { message: string | string[] } } }).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message];
				});
	}
	//submition handler
	const addBriefSummary = () => {
		// Legacy quirk kept as-is: reduce without an initial value uses the leading "" chunk as the seed.
		let summaryData: any = ([...convertUlToArrayDefault(briefSummary)] as any[]).reduce((prev: any, value: any) => {
			return [
				...prev,
				{
					bp: true,
					value,
				},
			];
		});

		summaryData = Array.isArray(summaryData) ? summaryData : [{ bp: false, value: summaryData }];

		if (isInputCompleted) {
			setResumeData((prevState) => {
				return {
					...prevState,
					summary: briefSummary,
				};
			});

			patchSection.mutate(
				{
					resumeId,
					section: RESUME_SECTIONS.SUMMARY,
					data: { data: summaryData },
				} as never,
				{
					onSuccess,
					onError,
				},
			);
		}
	};

	useEffect(() => {
		const validation = formValidation.validateBriefDetails(briefSummary);
		if (!validation) {
			setIsInputCompleted(false);
		} else {
			setIsInputCompleted(true);
		}
	}, [briefSummary]);

	const skipSection = () => {
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate("/resume/extras");
	};

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
	}, [resumeData]);

	return (
		<>
			<SkipSectionDialog open={showSkipDialog} onOpenChange={setShowSkipDialog} onSkip={skipSection} />

			<div className="min-h-screen">
				{serverErrors.map((item, index) => {
					return <ErrorToast error={item} key={index} />;
				})}
				<div>
					<div className="mb-2 flex justify-end">
						<Tips content={tips.professionalSummary} />
					</div>

					<SuggestionsPopover
						label="Briefly tell us about your background"
						hint="Pick a summary that fits. It is added to what you have written."
					>
						<div className="my-2 flex shrink-0 items-stretch">
							<Input
								type="text"
								placeholder={userInput}
								onChange={(e) => {
									setSearchValue(e.target.value);
								}}
								className="rounded-r-none"
							/>
							<Button type="button" className="rounded-l-none" aria-label="Search">
								<Search className="size-4" />
							</Button>
						</div>
						<EditorListItems
							editorListItems={summaryList}
							editorContent={briefSummary}
							setEditorContent={setBriefSummary}
							setEditorListItems={setSummaryList}
							className="h-auto min-h-0 flex-1"
						/>
					</SuggestionsPopover>

					<TextEditor
						value={briefSummary}
						setValue={setBriefSummary}
						placeholder="Write your summary here..."
						className="text-editor-auto-grow"
					/>

					<SectionActions className="flex items-center justify-between">
						<Button type="button" variant="ghost" onClick={() => previous()}>
							{previousTxt}
						</Button>
						<Button
							type="button"
							onClick={() => {
								if (!isInputCompleted) {
									setShowSkipDialog(true);
								}
								addBriefSummary();
							}}
						>
							{patchSection.isPending ? (
								<>
									Loading <Spinner />
								</>
							) : (
								"Continue"
							)}
						</Button>
					</SectionActions>
				</div>
			</div>
		</>
	);
};

export default ResumeBriefSummary;
